import { Injectable, inject, signal, computed, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type LanguageCode = 'en' | 'ar';
export type TextDirection = 'ltr' | 'rtl';

export interface LanguageOption {
  code: LanguageCode;
  /** Name shown in the switcher, written in that language. */
  label: string;
  dir: TextDirection;
  /** BCP-47 tag for Intl date/number formatting. */
  locale: string;
}

export const LANGUAGES: readonly LanguageOption[] = [
  { code: 'en', label: 'English', dir: 'ltr', locale: 'en-EG' },
  { code: 'ar', label: 'العربية', dir: 'rtl', locale: 'ar-EG' },
] as const;

const STORAGE_KEY = 'language';
const DEFAULT_LANGUAGE: LanguageCode = 'en';

/**
 * Owns the active language and keeps the document in sync with it.
 *
 * The KSA project shipped `@ngx-translate` wired up but effectively unused (one
 * template out of 165 called the `translate` pipe) and had no Arabic locale and
 * no RTL handling at all. Egypt needs both, so this service is the single place
 * that decides what language is active and flips `<html lang>` / `<html dir>`.
 *
 * Direction is driven off the `dir` attribute rather than a CSS class so that
 * native browser behaviour, Bootstrap RTL and Angular CDK all pick it up
 * without extra configuration.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  private readonly _current = signal<LanguageCode>(DEFAULT_LANGUAGE);

  /** Currently active language code. */
  readonly current = this._current.asReadonly();

  /** Text direction implied by the active language. */
  readonly dir = computed<TextDirection>(
    () => LANGUAGES.find(l => l.code === this._current())?.dir ?? 'ltr'
  );

  readonly isRtl = computed(() => this.dir() === 'rtl');

  readonly available = LANGUAGES;

  /**
   * Call once during app bootstrap, before the first render.
   * Resolves the language from storage, then the browser, then the default.
   */
  init(): void {
    this.translate.addLangs(LANGUAGES.map(l => l.code));
    // v18 renamed `setDefaultLang` to `setFallbackLang`.
    this.translate.setFallbackLang(DEFAULT_LANGUAGE);
    this.use(this.resolveInitialLanguage());
  }

  /** Switch the active language and persist the choice. */
  use(code: LanguageCode): void {
    const lang = LANGUAGES.find(l => l.code === code) ?? LANGUAGES[0];

    this._current.set(lang.code);
    this.translate.use(lang.code);

    const html = this.document.documentElement;
    html.setAttribute('lang', lang.code);
    html.setAttribute('dir', lang.dir);
    // Lets stylesheets target direction without reading the attribute directly.
    html.classList.toggle('rtl', lang.dir === 'rtl');
    html.classList.toggle('ltr', lang.dir === 'ltr');

    try {
      localStorage.setItem(STORAGE_KEY, lang.code);
    } catch {
      // Private browsing / storage disabled — the choice just won't persist.
    }
  }

  toggle(): void {
    this.use(this._current() === 'en' ? 'ar' : 'en');
  }

  private resolveInitialLanguage(): LanguageCode {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }

    if (this.isSupported(stored)) {
      return stored;
    }

    const browser = this.translate.getBrowserLang();
    if (this.isSupported(browser)) {
      return browser;
    }

    return DEFAULT_LANGUAGE;
  }

  private isSupported(code: string | null | undefined): code is LanguageCode {
    return !!code && LANGUAGES.some(l => l.code === code);
  }
}
